import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { GALLERY_DRAWER_HEADER_SX, ResponsiveDrawer } from 'components/common/ResponsiveDrawer/ResponsiveDrawer.component'
import type { ResolvedShellProduct } from 'models/purchaseFlowRevamp'

import { ProductInfoGallery } from './ProductInfoGallery.component'
import { ProductTermIcon } from './ProductTermIcon.component'
import { hasProductGroupDetails, useProductGroupDetails } from './useProductGroupDetails'

interface Props {
  /** The group to describe. Null closes the panel. */
  product: ResolvedShellProduct | null
  onClose: () => void
}

/**
 * "More information" about a product group: what the service delivers under
 * which terms, and examples of the result.
 *
 * Read-only and separate from `ProductDrawer` on purpose — this answers "what
 * is this?", the drawer answers "which one and how many?". Stacking is allowed
 * so it can be opened from the drawer later without closing it.
 */
export const ProductInfoDrawer: React.FC<Props> = ({ product, onClose }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const details = useProductGroupDetails(product?.productGroupCode)

  const hasContent = hasProductGroupDetails(details)

  return (
    <ResponsiveDrawer
      isOpen={Boolean(product)}
      onClose={onClose}
      title={product?.displayName ?? ''}
      headerSx={GALLERY_DRAWER_HEADER_SX}
      contentSx={{ p: '2.4rem' }}
      allowStacking
    >
      <Stack gap={2.4}>

        {details && details.gallery.length > 0 &&
          <ProductInfoGallery images={details.gallery} resetKey={product?.id} />
        }

        {product?.description &&
          <Typography variant="text-sm" color={GRAY_700}>
            {product.description}
          </Typography>
        }

        {details && details.terms.length > 0 &&
          <Stack gap={1.2}>

            <Typography variant="text-md" fontWeight={550} color={GRAY_900}>
              {t('purchase_flow_revamp:product_info.service_terms')}
            </Typography>

            {details.terms.map((term) => (
              <Stack key={term.text} direction="row" gap={1.2} alignItems="center">
                <ProductTermIcon icon={term.icon} />
                <Typography variant="text-sm" color={GRAY_900}>{term.text}</Typography>
              </Stack>
            ))}

          </Stack>
        }

        {details && details.deliverables.length > 0 &&
          <Stack gap={0.8}>

            <Typography variant="text-md" fontWeight={550} color={GRAY_900}>
              {t('purchase_flow_revamp:product_info.what_you_get')}
            </Typography>

            <Stack component="ul" gap={0.4} sx={{ m: 0, pl: 2.4 }}>
              {details.deliverables.map((item) => (
                <Typography key={item} component="li" variant="text-sm" color={GRAY_900}>
                  {item}
                </Typography>
              ))}
            </Stack>

          </Stack>
        }

        {!hasContent &&
          <Typography variant="text-sm" color={GRAY_700}>
            {t('purchase_flow_revamp:product_info.no_details')}
          </Typography>
        }

      </Stack>
    </ResponsiveDrawer>
  )
}
